import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Mail, Paperclip, Search, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Emails = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState({ category: '', search: '' });
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEmails();
  }, [filter]);

  const fetchEmails = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter.category) params.category = filter.category;
      if (filter.search) params.search = filter.search;
      
      const response = await axios.get(`${API}/emails`, { params });
      setEmails(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load emails');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await axios.post(`${API}/emails/sync`, { max_results: 50 });
      alert(`Sync complete! Fetched: ${response.data.emails_fetched}, Processed: ${response.data.emails_processed}`);
      fetchEmails();
    } catch (err) {
      alert(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      tasks: 'bg-blue-500',
      ideas: 'bg-purple-500',
      waiting: 'bg-yellow-500',
      schedule: 'bg-green-500',
      advertising: 'bg-red-500',
      newsletters: 'bg-indigo-500',
      personal: 'bg-pink-500',
      unclassified: 'bg-gray-500',
    };
    return colors[category] || 'bg-gray-500';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Emails</h1>
          <p className="text-muted-foreground">Browse and filter your classified emails</p>
        </div>
        <Button onClick={handleSync} disabled={syncing}>
          {syncing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Syncing...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Sync Gmail
            </>
          )}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search emails..."
                  value={filter.search}
                  onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filter.category} onValueChange={(v) => setFilter({ ...filter, category: v })}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Categories</SelectItem>
                <SelectItem value="tasks">Tasks</SelectItem>
                <SelectItem value="ideas">Ideas</SelectItem>
                <SelectItem value="waiting">Waiting</SelectItem>
                <SelectItem value="schedule">Schedule</SelectItem>
                <SelectItem value="advertising">Advertising</SelectItem>
                <SelectItem value="newsletters">Newsletters</SelectItem>
                <SelectItem value="personal">Personal</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Email List */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : emails.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No emails found. Try syncing with Gmail.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {emails.map((email) => (
            <Card key={email.id}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={getCategoryColor(email.category)}>
                        {email.category.replace('_', ' ')}
                      </Badge>
                      {email.priority && (
                        <Badge variant="outline">{email.priority}</Badge>
                      )}
                      {email.has_attachments && (
                        <Paperclip className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                    <h3 className="font-semibold truncate">{email.subject}</h3>
                    <p className="text-sm text-muted-foreground">{email.sender}</p>
                    <p className="text-sm mt-2 line-clamp-2">{email.snippet}</p>
                  </div>
                  <div className="text-sm text-muted-foreground ml-4">
                    {new Date(email.date).toLocaleDateString()}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Emails;